/**
 * Windowed page-dots (iOS-style pagination).
 *
 * Shows every dot for small decks; for larger ones it renders a fixed-width
 * window of `maxVisible` dots centered on the active card, shrinking the dots
 * at either edge to hint that more exist beyond the window. This keeps the
 * indicator a constant, compact width whether there are 5 cards or 500 -
 * unlike one-dot-per-card, which overflows.
 */

interface PaginatedDotsProps {
  /** Total number of items. */
  total: number;
  /** Active item index (0-based). */
  current: number;
  /**
   * Jump to an item when its dot is clicked. Omit for a read-only indicator
   * (e.g. the quiz, where questions are answered sequentially).
   */
  onSelect?: (index: number) => void;
  /** Max dots shown at once before windowing kicks in (odd number reads best). */
  maxVisible?: number;
}

export function PaginatedDots({
  total,
  current,
  onSelect,
  maxVisible = 7,
}: PaginatedDotsProps) {
  if (total <= 0) return null;

  const windowed = total > maxVisible;
  const count = windowed ? maxVisible : total;
  // Center the window on `current`, clamped to the deck ends.
  const start = windowed
    ? Math.max(
        0,
        Math.min(current - Math.floor(maxVisible / 2), total - maxVisible)
      )
    : 0;

  const edge = {
    hasLeft: windowed && start > 0,
    hasRight: windowed && start + count < total,
    end: start + count - 1,
  };

  const indices = Array.from({ length: count }, (_, k) => start + k);

  return (
    <div className='flex items-center justify-center gap-1.5'>
      {indices.map(i =>
        onSelect ? (
          <button
            key={i}
            type='button'
            aria-label={`Go to item ${i + 1}`}
            aria-current={i === current}
            onClick={() => onSelect(i)}
            className={dotClass(i, current, start, edge)}
          />
        ) : (
          // Read-only indicator (no jumping between items).
          <span
            key={i}
            aria-hidden='true'
            className={dotClass(i, current, start, edge)}
          />
        )
      )}
    </div>
  );
}

interface Edge {
  hasLeft: boolean;
  hasRight: boolean;
  end: number;
}

/** Edge dots shrink to signal "more beyond" the window. Never the active dot. */
function shrinkScale(i: number, start: number, edge: Edge): string {
  if ((edge.hasLeft && i === start) || (edge.hasRight && i === edge.end)) {
    return 'scale-50';
  }
  if (
    (edge.hasLeft && i === start + 1) ||
    (edge.hasRight && i === edge.end - 1)
  ) {
    return 'scale-75';
  }
  return '';
}

/** Brand indigo, the same accent as a selected quiz answer; raw blue was a
 *  second accent. Upcoming dots use the muted ink so they read as track. */
function dotColor(i: number, current: number): string {
  if (i === current) return 'bg-brand-600 dark:bg-primary';
  return i < current
    ? 'bg-brand-400 dark:bg-primary/50'
    : 'bg-muted-foreground/30';
}

/** Size + colour classes for a single dot, given its position in the window. */
function dotClass(
  i: number,
  current: number,
  start: number,
  edge: Edge
): string {
  const isActive = i === current;
  const width = isActive ? 'w-6' : 'w-1.5';
  const scale = isActive ? '' : shrinkScale(i, start, edge);
  return `h-1.5 rounded-full transition-all duration-200 ${width} ${scale} ${dotColor(i, current)}`;
}
